import { systemClock } from '@iris/shared';

/**
 * Push opt-in and install prompt (T4 §4.4–4.5, US-C17, US-C18, T0 §10.3): asked only after the first visit, never
 * from a page load; "Plus tard" hides a prompt for 14 days; refusing push keeps everything in the inbox.
 */
const SNOOZE_MS = 14 * 24 * 3_600_000;

/** The subscription the browser created, sent to `POST /push/subscriptions`. */
export type SaveSubscription = (body: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}) => Promise<void>;

interface InstallEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let installEvent: InstallEvent | null = null;
const listeners = new Set<() => void>();

/** Called once at startup: Chromium offers installation through this event (Android). */
export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    installEvent = event as InstallEvent;
    listeners.forEach((listener) => listener());
  });
  window.addEventListener('appinstalled', () => {
    installEvent = null;
    listeners.forEach((listener) => listener());
  });
}

export function onInstallAvailabilityChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const canPromptInstall = (): boolean => installEvent !== null;

export async function promptInstall(): Promise<boolean> {
  if (!installEvent) return false;
  const event = installEvent;
  installEvent = null;
  await event.prompt();
  return (await event.userChoice).outcome === 'accepted';
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone/iPad Safari: web push only once the app is on the home screen (iOS 16.4+). */
export function isIosBrowser(): boolean {
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && !isStandalone();
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  return pushSupported() ? Notification.permission : 'unsupported';
}

export function snoozed(prompt: 'push' | 'install'): boolean {
  try {
    const until = Number(window.localStorage.getItem(`iris.snooze.${prompt}`) ?? 0);
    return until > systemClock.now();
  } catch {
    return false;
  }
}

export function snooze(prompt: 'push' | 'install'): void {
  try {
    window.localStorage.setItem(`iris.snooze.${prompt}`, String(systemClock.now() + SNOOZE_MS));
  } catch {
    // private mode: the prompt comes back next time
  }
}

function keyBytes(base64url: string): Uint8Array {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

/**
 * "Activer": the browser's permission dialog, then a subscription with the VAPID key, sent to the API. Needs the
 * service worker (production build); in development it times out and says so.
 */
export async function enablePush(
  save: SaveSubscription,
  vapidPublicKey: string,
): Promise<'granted' | 'denied' | 'failed'> {
  if (!pushSupported()) return 'failed';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';
  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) =>
        window.setTimeout(() => reject(new Error('no service worker')), 5_000),
      ),
    ]);
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(vapidPublicKey),
      }));
    const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return 'failed';
    await save({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
    return 'granted';
  } catch {
    return 'failed';
  }
}
